import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus, X, Info, ExternalLink } from 'lucide-react';
import { calendarAPI } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const OTA_LOGOS = {
  airbnb: '🏠',
  bookingcom: '🔵',
  vrbo: '🌴',
  expedia: '✈️'
};

export default function PropertyCalendar({ propertyId, canManage = false }) {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockDates, setBlockDates] = useState([]);
  const [blockReason, setBlockReason] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);

  useEffect(() => {
    fetchCalendarData();
  }, [propertyId, currentDate]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const res = await calendarAPI.getPropertyCalendar(propertyId, {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      
      setCalendarData(res.data.data);
    } catch (error) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date || !calendarData) return [];
    
    return calendarData.events.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      const checkDate = new Date(date);
      
      return checkDate >= new Date(eventStart.setHours(0, 0, 0, 0)) && 
             checkDate < new Date(eventEnd.setHours(0, 0, 0, 0));
    });
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date) => {
    if (!canManage || !date) return;
    
    const dateStr = date.toISOString().split('T')[0];
    if (selectedDates.includes(dateStr)) {
      setSelectedDates(selectedDates.filter(d => d !== dateStr));
    } else {
      setSelectedDates([...selectedDates, dateStr]);
    }
  };

  const handleBlockDates = async () => {
    if (selectedDates.length === 0) {
      toast.error('Please select dates to block');
      return;
    }

    try {
      await calendarAPI.blockDates(propertyId, {
        dates: selectedDates,
        reason: blockReason || 'Blocked by owner'
      });
      toast.success(`${selectedDates.length} date(s) blocked`);
      setSelectedDates([]);
      setBlockReason('');
      setShowBlockModal(false);
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to block dates');
    }
  };

  const handleUnblockDate = async (date) => {
    try {
      await calendarAPI.unblockDates(propertyId, { dates: [date] });
      toast.success('Date unblocked');
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to unblock date');
    }
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPast = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const days = getDaysInMonth();

  if (loading) {
    return <div className="loading-center"><div className="spinner" /></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Calendar Header */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-ghost btn-icon" onClick={handlePrevMonth}>
              <ChevronLeft size={20} />
            </button>
            <h2 style={{ fontSize: 18, fontWeight: 700, minWidth: 180, textAlign: 'center' }}>
              {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button className="btn btn-ghost btn-icon" onClick={handleNextMonth}>
              <ChevronRight size={20} />
            </button>
            <button className="btn btn-sm btn-secondary" onClick={handleToday}>
              Today
            </button>
          </div>
          
          {canManage && (
            <div style={{ display: 'flex', gap: 8 }}>
              {selectedDates.length > 0 && (
                <button className="btn btn-sm btn-primary" onClick={() => setShowBlockModal(true)}>
                  <Plus size={16} /> Block {selectedDates.length} Date(s)
                </button>
              )}
            </div>
          )}
        </div>

        {/* Stats Bar */}
        {calendarData?.stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20, padding: 16, background: '#F9FAFB', borderRadius: 8 }}>
            {[
              { label: 'Booked', value: calendarData.stats.bookedDays, color: '#10B981' },
              { label: 'Blocked', value: calendarData.stats.blockedDays, color: '#EF4444' },
              { label: 'Available', value: calendarData.stats.availableDays, color: '#3B82F6' },
              { label: 'Occupancy', value: `${calendarData.stats.occupancyRate}%`, color: '#8B5CF6' }
            ].map(({ label, value, color }) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Calendar Grid */}
        <div>
          {/* Day Headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 8 }}>
            {DAYS.map(day => (
              <div key={day} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#6B7280', padding: 8 }}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {days.map((date, index) => {
              const events = date ? getEventsForDate(date) : [];
              const dateStr = date ? date.toISOString().split('T')[0] : '';
              const isSelected = selectedDates.includes(dateStr);
              const hasBooking = events.some(e => e.type === 'booking');
              const isBlocked = events.some(e => e.type === 'blocked');

              return (
                <div
                  key={index}
                  onClick={() => handleDateClick(date)}
                  style={{
                    minHeight: 100,
                    padding: 8,
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    background: !date ? '#F9FAFB' : isSelected ? '#EFF6FF' : isToday(date) ? '#FEF3C7' : '#FFFFFF',
                    cursor: canManage && date && !isPast(date) ? 'pointer' : 'default',
                    opacity: isPast(date) ? 0.6 : 1,
                    position: 'relative',
                    transition: 'all 0.2s'
                  }}
                >
                  {date && (
                    <>
                      <div style={{ 
                        fontSize: 14, 
                        fontWeight: isToday(date) ? 700 : 500, 
                        color: isToday(date) ? '#D97706' : '#111827',
                        marginBottom: 4
                      }}>
                        {date.getDate()}
                      </div>
                      
                      {events.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {events.slice(0, 2).map(event => (
                            <div
                              key={event.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEvent(event);
                              }}
                              style={{
                                padding: '4px 6px',
                                borderRadius: 4,
                                background: event.color,
                                color: '#FFFFFF',
                                fontSize: 10,
                                fontWeight: 600,
                                cursor: 'pointer',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 3
                              }}
                              title={event.type === 'blocked' ? 'Blocked' : `${event.title} - ${event.otaSource || 'Direct'}`}
                            >
                              {event.type === 'blocked' ? (
                                '🚫 Blocked'
                              ) : (
                                <>
                                  <span>{event.otaSource ? OTA_LOGOS[event.otaSource] : '📅'}</span>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 9 }}>
                                    {event.otaSource 
                                      ? event.otaSource.replace('bookingcom', 'Booking.com').toUpperCase()
                                      : event.title.split(' ')[0]
                                    }
                                  </span>
                                </>
                              )}
                            </div>
                          ))}
                          {events.length > 2 && (
                            <div style={{ fontSize: 10, color: '#6B7280', textAlign: 'center' }}>
                              +{events.length - 2} more
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, marginTop: 20, padding: 16, background: '#F9FAFB', borderRadius: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#10B981' }} />
            <span style={{ fontSize: 12, color: '#6B7280' }}>Direct Booking</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#FF5A5F' }} />
            <span style={{ fontSize: 12, color: '#6B7280' }}>Airbnb</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#003580' }} />
            <span style={{ fontSize: 12, color: '#6B7280' }}>Booking.com</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#0D47A1' }} />
            <span style={{ fontSize: 12, color: '#6B7280' }}>VRBO</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#EF4444' }} />
            <span style={{ fontSize: 12, color: '#6B7280' }}>Blocked</span>
          </div>
        </div>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div 
          className="modal-overlay" 
          onClick={() => setSelectedEvent(null)} 
          style={{ 
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)'
          }}
        >
          <div 
            className="modal-content" 
            onClick={e => e.stopPropagation()} 
            style={{ 
              maxWidth: 500, 
              zIndex: 10000,
              backgroundColor: '#FFFFFF',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid #E5E7EB'
            }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {selectedEvent.type === 'blocked' ? '🚫 Blocked Date' : `${selectedEvent.otaSource ? OTA_LOGOS[selectedEvent.otaSource] : '📅'} Booking Details`}
              </h3>
              <button className="modal-close" onClick={() => setSelectedEvent(null)}>×</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {selectedEvent.type === 'blocked' ? (
                <>
                  <div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Reason</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{selectedEvent.reason || 'Blocked by owner'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Date</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{new Date(selectedEvent.start).toLocaleDateString()}</div>
                  </div>
                  {canManage && (
                    <button 
                      className="btn btn-danger" 
                      onClick={() => {
                        handleUnblockDate(selectedEvent.start);
                        setSelectedEvent(null);
                      }}
                    >
                      Unblock This Date
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Guest</div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{selectedEvent.title}</div>
                    {selectedEvent.guestEmail && (
                      <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{selectedEvent.guestEmail}</div>
                    )}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Check-in</div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{new Date(selectedEvent.start).toLocaleDateString()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Check-out</div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{new Date(selectedEvent.end).toLocaleDateString()}</div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Nights</div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{selectedEvent.nights}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Total</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#10B981' }}>${selectedEvent.total?.toLocaleString()}</div>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Source</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className="badge" style={{ background: selectedEvent.color, color: '#FFFFFF' }}>
                        {selectedEvent.otaSource ? `${OTA_LOGOS[selectedEvent.otaSource]} ${selectedEvent.otaSource.toUpperCase()}` : '📅 Direct'}
                      </span>
                      <span className="badge badge-gray">{selectedEvent.status}</span>
                    </div>
                  </div>

                  <button 
                    className="btn btn-primary" 
                    onClick={() => {
                      navigate(`/bookings/${selectedEvent.id}`);
                      setSelectedEvent(null);
                    }}
                  >
                    <ExternalLink size={16} /> View Booking Details
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Block Dates Modal */}
      {showBlockModal && (
        <div 
          className="modal-overlay" 
          onClick={() => setShowBlockModal(false)} 
          style={{ 
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)'
          }}
        >
          <div 
            className="modal-content" 
            onClick={e => e.stopPropagation()} 
            style={{ 
              maxWidth: 400, 
              zIndex: 10000,
              backgroundColor: '#FFFFFF',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid #E5E7EB'
            }}>
            <div className="modal-header">
              <h3 className="modal-title">Block Dates</h3>
              <button className="modal-close" onClick={() => setShowBlockModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                  Selected Dates: {selectedDates.length}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280' }}>
                  {selectedDates.slice(0, 3).map(d => new Date(d).toLocaleDateString()).join(', ')}
                  {selectedDates.length > 3 && ` and ${selectedDates.length - 3} more...`}
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Reason (optional)</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  placeholder="e.g., Property maintenance, personal use..."
                  value={blockReason}
                  onChange={e => setBlockReason(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBlockModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleBlockDates}>
                <Plus size={16} /> Block Dates
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
